// MCHD Room Scheduler - Building Management Component
// Main interface for managing buildings, floors, and rooms

import React, { useState } from 'react';
import { Container, Title, Paper, Group, Button, TextInput, Select, Text, Stack, Alert, Box } from '@mantine/core';
import { IconPlus, IconSearch, IconRefresh, IconBuilding, IconBuildingStore, IconHome, IconChevronRight } from '@tabler/icons-react';
import { useBuildings, useCreateBuilding, useUpdateBuilding, useDeleteBuilding } from '../../../hooks/coordinator/useBuildingManagement';
import { BuildingList } from './BuildingList';
import { BuildingForm } from './BuildingForm';
import { FloorManagement } from './FloorManagement';
import { RoomManagement } from './RoomManagement';

/**
 * Building Management Component
 * Provides interface for coordinators to manage buildings, floors, and rooms
 */
export const BuildingManagement: React.FC = () => {
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [selectedFloor, setSelectedFloor] = useState<any>(null);
  const [showBuildingForm, setShowBuildingForm] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const {
    data: buildings,
    isLoading: loading,
    error,
    refetch
  } = useBuildings();

  const createBuildingMutation = useCreateBuilding();
  const updateBuildingMutation = useUpdateBuilding();
  const deleteBuildingMutation = useDeleteBuilding();

  /**
   * Handle creating a new building
   */
  const handleCreateBuilding = () => {
    setSelectedBuilding(null);
    setShowBuildingForm(true);
  };

  /**
   * Handle editing an existing building
   */
  const handleEditBuilding = (building: any) => {
    setSelectedBuilding(building);
    setShowBuildingForm(true);
  };

  /**
   * Handle building form submission
   */
  const handleBuildingFormSubmit = async (buildingData: any) => {
    try {
      if (selectedBuilding) {
        await updateBuildingMutation.mutateAsync({ id: selectedBuilding.id, data: buildingData });
      } else {
        await createBuildingMutation.mutateAsync(buildingData);
      }
      setShowBuildingForm(false);
      setSelectedBuilding(null);
    } catch (error) {
      console.error('Error saving building:', error);
    }
  };

  /**
   * Handle building deletion
   */
  const handleDeleteBuilding = async (buildingId: string) => {
    try {
      await deleteBuildingMutation.mutateAsync(buildingId);
    } catch (error) {
      console.error('Error deleting building:', error);
    }
  };

  /**
   * Handle building selection for floor management
   */
  const handleBuildingSelect = (building: any) => {
    setSelectedBuilding(building);
    setSelectedFloor(null); // Clear floor selection when changing building
  };

  /**
   * Handle floor selection for room management
   */
  const handleFloorSelect = (floor: any) => {
    setSelectedFloor(floor);
  };

  /**
   * Handle search
   */
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (value: string | null) => {
    setStatusFilter(value || '');
  };

  /**
   * Filter buildings based on search term and status
   */
  const filteredBuildings = React.useMemo(() => {
    if (!buildings) return [];
    
    return buildings.filter((building) => {
      const matchesSearch = searchTerm === '' || 
        building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (building.description && building.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesStatus = statusFilter === '' || 
        (statusFilter === 'active' && building.isActive) ||
        (statusFilter === 'inactive' && !building.isActive);
      
      return matchesSearch && matchesStatus;
    });
  }, [buildings, searchTerm, statusFilter]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Modern Header */}
        <Paper 
          p="xl" 
          withBorder 
          radius="lg"
          style={{ 
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
            border: '1px solid #e2e8f0'
          }}
        >
          <Group justify="space-between" align="flex-start">
            <Group gap="lg">
              {/* Icon with MCHD Brand Colors */}
              <Box
                p="md"
                style={{
                  background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(40, 168, 224, 0.15)'
                }}
              >
                <IconBuilding size={32} color="white" />
              </Box>
              
              {/* Title Section */}
              <div>
                <Title order={1} c="dark" fw={700} size="2rem" mb="xs">
                  Room Management
                </Title>
                <Text c="dimmed" size="lg" fw={400} style={{ lineHeight: 1.5 }}>
                  Manage buildings, floors, and rooms for efficient healthcare operations
                </Text>
                
                
              </div>
            </Group>
            
            {/* Action Buttons */}
            <Group gap="md">
              <Button
                variant="light"
                leftSection={<IconRefresh size={18} />}
                onClick={() => refetch()}
                loading={loading}
                radius="md"
                size="md"
                style={{ 
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  color: '#64748b',
                  fontWeight: 500
                }}
              >
                Refresh
              </Button>
              <Button
                leftSection={<IconPlus size={18} />}
                onClick={handleCreateBuilding}
                radius="md"
                size="md"
                style={{
                  background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '14px'
                }}
              >
                Add Building
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert color="red" title="Error">
            {error instanceof Error ? error.message : String(error)}
          </Alert>
        )}

        {/* Modern Filters */}
        <Paper 
          p="lg" 
          withBorder 
          radius="lg"
          style={{ 
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
            border: '1px solid #e2e8f0'
          }}
        >
          <Group gap="lg" align="end">
            <TextInput
              placeholder="Search buildings, descriptions..."
              leftSection={<IconSearch size={18} color="#64748b" />}
              value={searchTerm}
              onChange={(event) => handleSearch(event.currentTarget.value)}
              style={{ flex: 1 }}
              radius="md"
              size="md"
              styles={{
                input: {
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  fontSize: '14px',
                  height: '44px',
                  '&:focus': {
                    borderColor: '#28A8E0',
                    backgroundColor: 'white',
                    boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                  },
                  '&::placeholder': {
                    color: '#94a3b8'
                  }
                }
              }}
            />
            <Select
              placeholder="All Status"
              data={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active Only' },
                { value: 'inactive', label: 'Inactive Only' }
              ]}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              clearable
              radius="md"
              size="md"
              styles={{
                input: {
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  fontSize: '14px',
                  height: '44px',
                  '&:focus': {
                    borderColor: '#28A8E0',
                    backgroundColor: 'white',
                    boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                  }
                }
              }}
            />
          </Group>
        </Paper>

        {/* Modern Breadcrumb Navigation */}
        {(selectedBuilding || selectedFloor) && (
          <Paper 
            p="lg" 
            withBorder 
            radius="md"
            style={{ 
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)'
            }}
          >
            <Group gap="sm" align="center">
              <Text size="sm" c="dimmed" fw={500}>Navigation:</Text>
              
              {/* Buildings Breadcrumb */}
              <Button
                variant="subtle"
                size="sm"
                leftSection={<IconBuilding size={16} />}
                onClick={() => {
                  setSelectedBuilding(null);
                  setSelectedFloor(null);
                }}
                style={{
                  fontWeight: 500,
                  color: '#28A8E0'
                }}
              >
                Buildings
              </Button>
              
              {selectedBuilding && (
                <>
                  <IconChevronRight size={14} color="#94a3b8" />
                  <Button
                    variant="subtle"
                    size="sm"
                    leftSection={<IconBuildingStore size={16} />}
                    onClick={() => {
                      setSelectedFloor(null);
                    }}
                    style={{
                      fontWeight: selectedFloor ? 500 : 600,
                      color: selectedFloor ? '#28A8E0' : '#1e293b'
                    }}
                  >
                    {selectedBuilding.name}
                  </Button>
                </>
              )}
              
              {selectedFloor && (
                <>
                  <IconChevronRight size={14} color="#94a3b8" />
                  <Button
                    variant="subtle"
                    size="sm"
                    leftSection={<IconHome size={16} />}
                    style={{
                      fontWeight: 600,
                      color: '#1e293b'
                    }}
                  >
                    Floor {selectedFloor.number}
                  </Button>
                </>
              )}
            </Group>
          </Paper>
        )}

        {/* State-Based Conditional Content */}
        <Paper 
          withBorder 
          radius="md"
          style={{ 
            background: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)'
          }}
        >
          {/* Root Level: Buildings List */}
          {!selectedBuilding && !selectedFloor && (
            <BuildingList
              buildings={filteredBuildings}
              loading={loading}
              onEdit={handleEditBuilding}
              onDelete={handleDeleteBuilding}
              onSelect={handleBuildingSelect}
            />
          )}

          {/* Child Level: Floors Management */}
          {selectedBuilding && !selectedFloor && (
            <FloorManagement
              building={selectedBuilding}
              onFloorSelect={handleFloorSelect}
            />
          )}

          {/* Grandchild Level: Rooms Management */}
          {selectedBuilding && selectedFloor && (
            <RoomManagement
              floor={selectedFloor}
              building={selectedBuilding}
            />
          )}
        </Paper>

        {/* Building Form Modal */}
        {showBuildingForm && (
          <BuildingForm
            building={selectedBuilding}
            onSubmit={handleBuildingFormSubmit}
            onClose={() => {
              setShowBuildingForm(false);
              setSelectedBuilding(null);
            }}
          />
        )}
      </Stack>
    </Container>
  );
};
